// Manager login page — V1 scope.
//
// `LoginPage` is a thin composition of the design-system primitives:
//   - `BrandMark` for the Aquarius brand image.
//   - `Typography` for the eyebrow / heading / body text.
//   - `LoginForm` (organism) for the form itself — owns field state via
//     Tanstack Form and the submit handler is injected via `on_submit`.
//
// The `on_submit` callback POSTs to `/api/auth/sign-in/username`, which is
// handled by `src/routes/api/auth/$.tsx`'s `server.handlers.POST`
// short-circuit. The route handler invokes the `signIn` application use
// case so every successful login writes `manager.login_succeeded` to
// `tenancy_audit_log` and emits the observability event. Better Auth sets
// the session cookie via the response's `Set-Cookie` header; on success
// the page invalidates the router and navigates to `/live-in-club`.

import { useNavigate, useRouter } from '@tanstack/react-router';

import { BrandMark } from '~/_shared/design-system/ui/atoms/brand-mark/brand-mark';
import { Typography } from '~/_shared/design-system/ui/atoms/typography/typography';
import type { LoginFormSubmitResult } from '~/_shared/design-system/ui/organisms/forms/login-form/login-form';
import { LoginForm } from '~/_shared/design-system/ui/organisms/forms/login-form/login-form';

import './login-page.css';

export function LoginPage() {
	const navigate = useNavigate();
	const router = useRouter();

	async function on_submit(values: {
		username: string;
		password: string;
	}): Promise<LoginFormSubmitResult> {
		try {
			const response = await fetch('/api/auth/sign-in/username', {
				method: 'POST',
				headers: { 'Content-Type': 'application/json' },
				credentials: 'same-origin',
				body: JSON.stringify(values),
			});
			if (response.ok) {
				// Invalidate the router so the next navigation re-evaluates
				// beforeLoad guards (which now see the fresh session cookie).
				await router.invalidate();
				await navigate({ to: '/live-in-club' });
				return { ok: true };
			}
			// Better Auth returns 401 / 403 / 422 for credential failures and
			// validation errors. Treat all as the unified "invalid credentials"
			// case per the Talent Privacy + Security by Default principles.
			if (response.status >= 400 && response.status < 500) {
				return {
					ok: false,
					error: 'Username or password is incorrect.',
				};
			}
			return {
				ok: false,
				error: 'Something went wrong. Try again in a moment.',
			};
		} catch {
			return {
				ok: false,
				error: 'Something went wrong. Try again in a moment.',
			};
		}
	}

	return (
		<main className='login-page'>
			<header className='login-page__header'>
				<BrandMark
					id='login-page-brand-mark'
					src='/brand/aquarius-icon.png'
					alt='Aquarius'
					size='md'
				/>
				<div className='login-page__header-text'>
					<Typography
						id='login-page-eyebrow'
						variant='eyebrow'
						as='p'
						className='login-page__eyebrow'
					>
						Aquarius Talent Promoter
					</Typography>
					<Typography
						id='login-page-heading'
						variant='subheading'
						as='h1'
						className='login-page__heading'
					>
						Manager App
					</Typography>
				</div>
			</header>

			<section>
				<Typography id='login-page-subheading' variant='heading' as='h2'>
					Sign in
				</Typography>
				<Typography
					id='login-page-help'
					variant='body'
					className='login-page__help'
				>
					Sign in with your Club's credentials. If you've forgotten it, speak to
					your Club's Owner.
				</Typography>

				<LoginForm id='manager-login' on_submit={on_submit} />
			</section>
		</main>
	);
}
